import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AboutPage } from '@/features/about/AboutPage';
import { InvitePage } from '@/features/auth/InvitePage';
import { ProfilePage } from '@/features/auth/ProfilePage';
import { LoginPage } from '@/features/auth/LoginPage';
import { AccessLogTab } from '@/features/admin/AccessLogTab';
import { ActivationTab } from '@/features/admin/ActivationTab';
import { DataTab } from '@/features/admin/DataTab';
import { FeedbackTab } from '@/features/admin/FeedbackTab';
import { LearnedTab } from '@/features/admin/LearnedTab';
import { ReliabilityTab } from '@/features/admin/ReliabilityTab';
import { ErrorsTab } from '@/features/admin/ErrorsTab';
import { DocumentsTab } from '@/features/admin/DocumentsTab';
import { AdminPage } from '@/features/admin/AdminPage';
import { DestinationsTab } from '@/features/admin/DestinationsTab';
import { MailboxesTab } from '@/features/admin/MailboxesTab';
import { UsersTab } from '@/features/admin/UsersTab';
import { ActivationPage } from '@/features/activation/ActivationPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { EmailDetailPage } from '@/features/emails/EmailDetailPage';
import { EmailsPage } from '@/features/emails/EmailsPage';
import { QuoteEditorPage } from '@/features/quotes/QuoteEditorPage';
import { QuotesPage } from '@/features/quotes/QuotesPage';
import { StatsTab } from '@/features/stats/StatsTab';
import { ScreenErrorBoundary } from '@/shared/ui/ScreenErrorBoundary';

import { AppLayout } from './layout/AppLayout';
import { RequireAdmin, RequirePlatform } from './layout/RequireAdmin';
import { RequireAuth } from './layout/RequireAuth';
import { RequireFeature } from './layout/RequireFeature';
import { AppProviders } from './providers';

/**
 * Route table.
 *
 * Exported without the router on purpose: the tests mount it inside a MemoryRouter to
 * drive navigation without a browser.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      {/* The invitation link (contract v10.10): public, the person has no session yet. */}
      <Route path="/invitacion/:token" element={<InvitePage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/correos" replace />} />
          <Route path="/correos" element={<EmailsPage />} />
          <Route path="/correos/:emailId" element={<EmailDetailPage />} />
          <Route path="/presupuestos" element={<QuotesPage />} />
          <Route path="/presupuestos/:quoteId" element={<QuoteEditorPage />} />
          <Route path="/clientes" element={<ClientsPage />} />

          {/* Fase 1 (ADR 0016 del back): apagado por defecto. Sin la funcion, va a la
              bandeja igual que una ruta desconocida. */}
          <Route element={<RequireFeature feature="document_archive" />}>
            <Route path="/documentos" element={<DocumentsPage />} />
          </Route>

          <Route path="/acerca-de" element={<AboutPage />} />
          <Route path="/perfil" element={<ProfilePage />} />

          {/* Only an administrator (Ignira or the is_admin switch, contract v10.11), and
              the API refuses the data to anybody else regardless: the guard is for the
              user, not for security. */}
          <Route element={<RequireAdmin />}>
            <Route path="/activacion" element={<ActivationPage />} />
            <Route path="/administracion" element={<AdminPage />}>
              <Route index element={<Navigate to="/administracion/usuarios" replace />} />
              <Route path="usuarios" element={<UsersTab />} />
              <Route path="buzones" element={<MailboxesTab />} />
              <Route path="destinos" element={<DestinationsTab />} />
              <Route path="activacion" element={<ActivationTab />} />
              {/* Contract v10.35: for whoever administers, who wrote the notes. */}
              <Route path="aprendido" element={<LearnedTab />} />
              {/* The platform half: Ignira alone. */}
              <Route element={<RequirePlatform />}>
                <Route path="accesos" element={<AccessLogTab />} />
                <Route path="datos" element={<DataTab />} />
                <Route path="correcciones" element={<FeedbackTab />} />
                <Route path="incidencias" element={<ErrorsTab />} />
                <Route path="fiabilidad" element={<ReliabilityTab />} />
                <Route path="estadisticas" element={<StatsTab />} />
                <Route element={<RequireFeature feature="document_archive" />}>
                  <Route path="documentos" element={<DocumentsTab />} />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Estadísticas lives in Administración since 2026-10-02: old links land there. */}
          <Route
            path="/estadisticas"
            element={<Navigate to="/administracion/estadisticas" replace />}
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/correos" replace />} />
    </Routes>
  );
}

export function AppRouter() {
  return (
    <ScreenErrorBoundary page>
      <AppProviders>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AppProviders>
    </ScreenErrorBoundary>
  );
}
